import { useCallback, useEffect, useState } from "react";
import { getSmartLinkConfig, requestSmartRedirect } from "../services/smartLinkApi";
import {
  isEligibleCard,
  isInteractiveTarget,
  redirectActionButton,
  cooldownActive,
} from "../utils/smartLinkPlacement";

// Shared hook for the three blog-list pages (Read Blogs, Profile, Author
// Profile): every Nth card — N from the admin's Card Interval — sends the
// user to an admin Smart Link on its first click, once per cooldown.
//
//   const smartLinks = useSmartLinks("readblogs");
//   <div className="blog-card" {...smartLinks.cardProps(position)}>
//
// cardProps() is {} for a card that isn't eligible, so those cards are
// untouched. On an eligible card the first click on the card OR on its like /
// comment / share button opens the link. During the cooldown an eligible card
// behaves like any other.
//
// The config is shared at module level, so moving between the three pages
// keeps one view of the cooldown, and it is re-read from the server on every
// mount, on Back (pageshow) and when the tab becomes visible again — a
// refresh can never "forget" a cooldown. The server enforces the cooldown and
// picks the destination on every click; this side only avoids a pointless
// request while a cooldown it already knows about is running.

let shared = { config: null, offset: 0 };
let inflight = null;
let redirecting = false;
let replaying = false;
const listeners = new Set();

function setShared(patch) {
  shared = { ...shared, ...patch };
  listeners.forEach((l) => l(shared));
}

function patchConfig(patch) {
  if (shared.config) setShared({ config: { ...shared.config, ...patch } });
}

export function refreshSmartLinkConfig() {
  if (inflight) return inflight;
  inflight = getSmartLinkConfig()
    .then((cfg) => {
      const serverNow = Date.parse(cfg.serverNow);
      setShared({ config: cfg, offset: Number.isFinite(serverNow) ? serverNow - Date.now() : 0 });
    })
    .catch(() => {
      // Non-blocking: no config means no card is eligible, and every card
      // simply keeps its normal behaviour.
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

// Test-only: module state outlives a single render tree.
export function resetSmartLinksForTests() {
  shared = { config: null, offset: 0 };
  inflight = null;
  redirecting = false;
  replaying = false;
  listeners.clear();
}

export default function useSmartLinks(page) {
  const [state, setState] = useState(shared);

  useEffect(() => {
    listeners.add(setState);
    setState(shared);
    refreshSmartLinkConfig();
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshSmartLinkConfig();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      listeners.delete(setState);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, []);

  // Asks the server for the redirect; resolves true when the page is leaving.
  const claim = useCallback(
    async (position) => {
      redirecting = true;
      try {
        const r = await requestSmartRedirect(position, page);
        if (r.cooldownUntil !== undefined) patchConfig({ cooldownUntil: r.cooldownUntil });
        if (r.redirect && /^https?:\/\//i.test(r.url || "")) {
          // Same tab, so the browser's Back button returns here.
          window.location.assign(r.url);
          return true;
        }
        // The page's view of the interval / links was stale.
        if (r.reason === "not_eligible" || r.reason === "no_links") refreshSmartLinkConfig();
      } catch {
        // A failed claim leaves the card's normal behaviour in place.
      } finally {
        redirecting = false;
      }
      return false;
    },
    [page]
  );

  const handleCardClick = useCallback(
    (position, e) => {
      if (e.defaultPrevented) return;
      if (isInteractiveTarget(e.target, e.currentTarget)) return;
      const selection = typeof window.getSelection === "function" ? String(window.getSelection() || "") : "";
      if (selection.trim()) return; // selecting text is not a click on the card
      if (redirecting) return; // double-click / second card while one is in flight
      if (cooldownActive(shared.config?.cooldownUntil, shared.offset)) return;
      claim(position);
    },
    [claim]
  );

  // Capture phase, so it runs BEFORE the like / comment / share button's own
  // handler and can hold it back on the first click.
  const handleActionCapture = useCallback(
    async (position, e) => {
      if (replaying) return; // our own re-click below: let the button act
      const button = redirectActionButton(e.target, e.currentTarget);
      if (!button || button.disabled) return;
      if (!redirecting && cooldownActive(shared.config?.cooldownUntil, shared.offset)) return;

      e.preventDefault();
      e.stopPropagation();
      if (redirecting) return; // the page is already on its way out

      const left = await claim(position);
      if (left || !button.isConnected) return;
      // No redirect (cooldown on another tab, server error): the tap still
      // likes / comments / shares, as if the smart link weren't there.
      replaying = true;
      try {
        button.click();
      } finally {
        replaying = false;
      }
    },
    [claim]
  );

  const cfg = state.config;

  const isEligible = useCallback(
    (position) => !!cfg && cfg.activeLinks > 0 && isEligibleCard(position, cfg.cardInterval),
    [cfg]
  );

  const cardProps = useCallback(
    (position) =>
      isEligible(position)
        ? {
            onClick: (e) => handleCardClick(position, e),
            onClickCapture: (e) => handleActionCapture(position, e),
            "data-smart-link": "eligible",
          }
        : {},
    [isEligible, handleCardClick, handleActionCapture]
  );

  return { config: cfg, isEligible, cardProps };
}
